import {parseNativeOAuthCallback} from './socialAuth.js';

export async function isNativeShell() {
  const {Capacitor} = await import('@capacitor/core');
  return Capacitor.isNativePlatform();
}

export async function openNativeAuthUrl(url) {
  const parsed = new URL(url);
  if (parsed.protocol !== 'https:') throw new Error('The authentication provider URL is invalid.');
  const {Browser} = await import('@capacitor/browser');
  await Browser.open({url: parsed.toString(), presentationStyle: 'popover'});
}

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));
const consumedNativeCallbacks = new Set();
const MAX_REMEMBERED_CALLBACKS = 20;

function claimNativeAuthCallback(callback) {
  const key = callback.code
    ? `code:${callback.code}`
    : `error:${callback.error}:${callback.returnTo}`;
  if (consumedNativeCallbacks.has(key)) return false;
  consumedNativeCallbacks.add(key);
  if (consumedNativeCallbacks.size > MAX_REMEMBERED_CALLBACKS) {
    consumedNativeCallbacks.delete(consumedNativeCallbacks.values().next().value);
  }
  return true;
}

export function restoreNativeInputFocus(dependencies = {}) {
  const documentObject = dependencies.documentObject || globalThis.document;
  const windowObject = dependencies.windowObject || globalThis.window;
  documentObject?.activeElement?.blur?.();
  windowObject?.focus?.();
}

export async function closeNativeAuthBrowser(dependencies = {}) {
  const Browser = dependencies.Browser || (await import('@capacitor/browser')).Browser;
  const waitForTimeout = dependencies.waitForTimeout || wait;
  let finishedListener;
  let markFinished;
  const finished = new Promise((resolve) => { markFinished = resolve; });
  try {
    finishedListener = await Browser.addListener?.('browserFinished', markFinished);
    await Browser.close();
    await Promise.race([finished, waitForTimeout(dependencies.timeoutMs ?? 750)]);
  } catch {
    // Android closes the custom tab during the app-link handoff.
  } finally {
    await finishedListener?.remove?.();
    restoreNativeInputFocus(dependencies);
  }
}

export async function subscribeToNativeAuth({client, onComplete, onError, dependencies = {}}) {
  const detectNativeShell = dependencies.isNativeShell || isNativeShell;
  if (!client || !(await detectNativeShell())) return () => {};
  const App = dependencies.App || (await import('@capacitor/app')).App;
  const closeBrowser = dependencies.closeNativeAuthBrowser || closeNativeAuthBrowser;
  const consume = async ({url} = {}) => {
    const callback = parseNativeOAuthCallback(url);
    if (!callback) return;
    if (!claimNativeAuthCallback(callback)) return;
    if (callback.error || !callback.code) {
      await closeBrowser();
      onError?.();
      return;
    }
    await closeBrowser();
    const {error} = await client.auth.exchangeCodeForSession(callback.code);
    if (error) {
      onError?.();
      return;
    }
    onComplete?.(callback.returnTo);
  };

  const listener = await App.addListener('appUrlOpen', consume);
  const launch = await App.getLaunchUrl();
  if (launch?.url) await consume(launch);
  return () => listener.remove();
}
